"use client";

import { cn } from "@/lib/utils";
import { ArrowLeftCircleIcon, MailIcon } from "lucide-react";
import React from "react";

import { Cookies, Routes } from "@/api";
import { logInWithEmailAction } from "@/app/actions/user/user-actions";
import { zodResolver } from "@hookform/resolvers/zod";
import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";

import { useClientReady } from "@/hooks/layout/use-client-ready";
import useMeasure from "@/hooks/layout/use-measure";
import { useToggle } from "@/hooks/use-toggle";
import { motion, MotionConfig, Transition } from "motion/react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import { ServerFormItem, ServerFormMessage } from "../ui/form-server";
import { DiscordIcon } from "../ui/icons/discord-icon";
import { GoogleIcon } from "../ui/icons/google-icon";
import { XIcon } from "../ui/icons/x-icon";
import { Input } from "../ui/input";
import { LoadingButton } from "../ui/loading-button";
import { AuthenticationEmailSubmitButton } from "./email/authentication-demo-submit-button";

const defaultTransition: Transition = {
  duration: 0.5,
  type: "spring",
  bounce: 0,
};

const authFormSchema = z.object({
  providerId: z
    .union([
      z.literal("discord"),
      z.literal("google"),
      z.literal("twitter"),
      z.literal("email"),
    ])
    .optional(),
});

type AuthFormValues = z.infer<typeof authFormSchema>;

const defaultValues: AuthFormValues = {
  providerId: undefined,
};

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {
  callbackUrl: string | null;
}

export function UserAuthForm({
  className,
  callbackUrl,
  ...props
}: UserAuthFormProps) {
  const t = useTranslations("Authentication.form");
  // const turnstile = useTurnstileCaptcha();
  const searchParams = useSearchParams();
  const [ref, measure] = useMeasure<HTMLDivElement>();
  const form = useForm<AuthFormValues>({
    defaultValues,
    resolver: zodResolver(authFormSchema),
    mode: "all",
  });
  const emailToggle = useToggle();
  const isClientReady = useClientReady();
  const providerId = form.watch("providerId");

  const [validationError, setValidationError] =
    React.useState<Awaited<ReturnType<typeof logInWithEmailAction>>["error"]>();

  const getAffiliateCodeAsParams = React.useCallback(() => {
    const affiliateCode = searchParams?.get(Cookies.affiliate) ?? "";
    if (!affiliateCode) {
      return new URLSearchParams();
    }

    return new URLSearchParams({
      [Cookies.affiliate]: affiliateCode,
    });
  }, [searchParams]);

  // const checkCaptcha = React.useCallback(async () => {
  //   const captchaValidated = await turnstile.verifyCaptcha();
  //   if (!captchaValidated) {
  //     // Notice, we used invisible captcha, so we need to tell user
  //     // about the captcha validation failure
  //     toast.error("Captcha validation failed. Please try again.");
  //     // Reset captcha widget so that user can try again
  //     turnstile.resetWidget();
  //     return false;
  //   } else {
  //     return true;
  //   }
  // }, [turnstile]);

  const logInWithEmail = React.useCallback(async (formData: FormData) => {
    // const captchaValidated = await checkCaptcha();
    // if (!captchaValidated) {
    //   return;
    // }

    // const isDisposable = await checkIfDisposableEmailAction(
    //   formData.get("email") as string,
    // );
    // if (isDisposable) {
    //   toast.error("Email address is not allowed");
    //   return;
    // }

    const result = await logInWithEmailAction(formData);
    if (!result) {
      return;
    }

    if (!result?.success) {
      if (typeof result.error === "string") {
        toast.error("An error occurred. Please try again.");
      } else {
        setValidationError(result.error);
      }
    }
  }, []);

  const onSubmit = React.useCallback(async () => {
    const providerId = form.getValues("providerId");
    const params = getAffiliateCodeAsParams();

    if (!providerId) {
      form.setError("providerId", {
        type: "manual",
        message: "Please select a provider to sign in with.",
      });
      return;
    }

    // const captchaValidated = await checkCaptcha();
    // if (!captchaValidated) {
    //   form.reset();
    //   return;
    // }

    let url = callbackUrl ?? Routes.PORTFOLIOS;
    if (params.size > 0) {
      url += `?${params.toString()}`;
    }

    await signIn(
      providerId,
      {
        callbackUrl: url,
      },
      params,
    );

    form.reset();
  }, [callbackUrl, form, getAffiliateCodeAsParams]);

  return (
    <MotionConfig transition={defaultTransition}>
      <motion.div
        initial={false}
        animate={!isClientReady ? undefined : { height: measure.height }}
        className="overflow-hidden px-1"
      >
        <div ref={ref} className="flex flex-col gap-4">
          {!emailToggle.value ? (
            <motion.div
              className="flex flex-col gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Form {...form}>
                <div className={cn("grid gap-4", className)} {...props}>
                  {/* <form onSubmit={onSubmit}>
          <div className="grid gap-2">
            <div className="grid gap-1">
              <Label className="sr-only" htmlFor="email">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                placeholder="name@example.com"
                type="email"
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect="off"
                disabled={isLoading}
              />
            </div>
            <Button disabled={isLoading}>
              {isLoading && <Loader2Icon className="mr-2 size-4 animate-spin" />}
              Sign In with Email
            </Button>
          </div>
        </form> */}
                  {/* <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or continue with
            </span>
          </div>
        </div> */}
                  <FormField
                    control={form.control}
                    name="providerId"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <LoadingButton
                            ref={field.ref}
                            name={field.name}
                            value={field.value}
                            onBlur={field.onBlur}
                            variant="outline"
                            type="button"
                            loading={providerId === "discord"}
                            disabled={providerId === "discord"}
                            className="sign-in sign-in-discord w-full"
                            onClick={(e) => {
                              e.preventDefault();
                              field.onChange("discord");
                              form.handleSubmit(onSubmit)();
                            }}
                            gtmEvent={{
                              event: "sign_in",
                              eventCategory: "authentication",
                              eventAction: "click",
                              eventLabel: "sign_in_with_discord",
                            }}
                          >
                            <DiscordIcon className="mr-2 size-4 fill-foreground" />
                            {t("continue.discord")}
                          </LoadingButton>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="providerId"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <LoadingButton
                            ref={field.ref}
                            name={field.name}
                            value={field.value}
                            onBlur={field.onBlur}
                            variant="outline"
                            type="button"
                            disabled={providerId === "google"}
                            loading={providerId === "google"}
                            className="sign-in sign-in-google w-full"
                            onClick={(e) => {
                              e.preventDefault();
                              field.onChange("google");
                              form.handleSubmit(onSubmit)();
                            }}
                            gtmEvent={{
                              event: "sign_in",
                              eventCategory: "authentication",
                              eventAction: "click",
                              eventLabel: "sign_in_with_google",
                            }}
                          >
                            <GoogleIcon className="mr-2 size-4 fill-foreground" />
                            {t("continue.google")}
                          </LoadingButton>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="providerId"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <LoadingButton
                            ref={field.ref}
                            name={field.name}
                            value={field.value}
                            onBlur={field.onBlur}
                            variant="outline"
                            type="button"
                            loading={providerId === "twitter"}
                            disabled={providerId === "twitter"}
                            className="sign-in sign-in-twitter w-full"
                            onClick={(e) => {
                              e.preventDefault();
                              field.onChange("twitter");
                              form.handleSubmit(onSubmit)();
                            }}
                            gtmEvent={{
                              event: "sign_in",
                              eventCategory: "authentication",
                              eventAction: "click",
                              eventLabel: "sign_in_with_twitter",
                            }}
                          >
                            <XIcon className="mr-2 size-4 fill-foreground" />
                            {t("continue.x")}
                          </LoadingButton>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor={field.name}>Referral Code*</FormLabel>
                <FormControl>
                  <Input placeholder="" {...field} />
                </FormControl>
                <FormDescription>
                  *Optional. <br />
                  If you have a referral code, enter it here.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          /> */}
                </div>
              </Form>
              <p className="text-center">
                {t.rich("email.cta", {
                  button: (chunks) => {
                    return (
                      <button
                        className="underline underline-offset-2"
                        onClick={emailToggle.setOn}
                      >
                        {chunks}
                      </button>
                    );
                  },
                })}
              </p>
            </motion.div>
          ) : (
            <motion.div
              className="flex flex-col gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="flex gap-4">
                <Button
                  onClick={emailToggle.setOff}
                  size="icon"
                  variant="ghost"
                >
                  <ArrowLeftCircleIcon className="size-6" />
                </Button>
                <p>{t("email.description")}</p>
              </div>
              <form action={logInWithEmail} className="space-y-2">
                <div className="space-y-2">
                  <ServerFormItem>
                    <Input
                      id="email"
                      name="email"
                      placeholder="satoshi@bitcoin.com"
                    />
                  </ServerFormItem>
                  <ServerFormMessage
                    id="email-error-message"
                    error={validationError?.email}
                    aria-errormessage={validationError?.email?._errors.join(
                      ", ",
                    )}
                  />
                </div>
                <AuthenticationEmailSubmitButton
                  className="w-full"
                  variant="outline"
                >
                  <MailIcon className="mr-2 size-4" />
                  {t("continue.email")}
                </AuthenticationEmailSubmitButton>
              </form>
            </motion.div>
          )}
          {/* {turnstile.content} */}
        </div>
      </motion.div>
    </MotionConfig>
  );
}
